// Booking's reads for the staff work of Phases 4 and 5 (rentals and
// sponsorships plan; ADR 0045). Booking stays the record: these are what
// Hub's quote builder, contract, insurance, documents and money tabs and the
// collections list show, each with Booking's "as of" time. Bearer-only on
// Booking's HUB_READ_SECRET, SELECT only, like every other Hub read
// (ADR 0041). Money is whole cents. Never sent: card or bank details, Stripe
// or QuickBooks ids, file keys, signing tokens, COI policy numbers, signer
// IPs, email addresses.
//
//   GET /api/internal/hub/v1/rentals/:id/workbench
//   GET /api/internal/hub/v1/sponsorships/:id/workbench
//   GET /api/internal/hub/v1/rentals/:id/money
//   GET /api/internal/hub/v1/sponsorships/:id/money
//   GET /api/internal/hub/v1/collections
//   GET /api/internal/hub/v1/refunds?key=hub:<id>

import { z } from 'zod'
import { BOOKING_COLLECTION_STATUSES } from './source-booking-commands'

export const bookingWorkbenchPath = (kind: 'rental' | 'sponsorship', id: string) => `/${kind === 'rental' ? 'rentals' : 'sponsorships'}/${encodeURIComponent(id)}/workbench`
export const bookingRenterPath = (id: string) => `/rentals/${encodeURIComponent(id)}/renter`
export const bookingMoneyPath = (kind: 'rental' | 'sponsorship', id: string) => `/${kind === 'rental' ? 'rentals' : 'sponsorships'}/${encodeURIComponent(id)}/money`
export const BOOKING_COLLECTIONS_PATH = '/collections'
export const BOOKING_REFUND_BY_KEY_PATH = '/refunds'

const Id = z.string().min(1).max(64)
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const Iso = z.string().max(40)
const Text = (max: number) => z.string().max(max)
const Minor = z.number().int()
const Version = z.string().max(20)

const RecordHead = z.object({ kind: z.enum(['rental', 'sponsorship']), id: Id, version: Version, status: Text(40), title: Text(300) })

// ------------------------------------------------------------- rental work

export const BookingQuoteSpace = z.object({
  sourceId: Id,
  spaceId: Id,
  venue: Text(200).nullable(),
  space: Text(200).nullable(),
  dayType: z.enum(['event', 'setup', 'cleanup']),
  // Booking's local wall time (America/Chicago), 'YYYY-MM-DDTHH:MM', and the
  // same instants in ISO.
  startLocal: Text(16).nullable(),
  endLocal: Text(16).nullable(),
  startsAt: Iso.nullable(),
  endsAt: Iso.nullable(),
  eventStartLocal: Text(16).nullable(),
  eventEndLocal: Text(16).nullable(),
  rateType: z.enum(['hourly', 'half_day', 'full_day', 'custom']),
  rateMinor: Minor.nullable(),
  quantity: z.number(),
  totalMinor: Minor.nullable(),
  notes: Text(1000).nullable(),
})
export type BookingQuoteSpace = z.infer<typeof BookingQuoteSpace>

export const BookingQuoteLine = z.object({ sourceId: Id, description: Text(500), quantity: z.number(), unitMinor: Minor, totalMinor: Minor, category: Text(100).nullable() })
export type BookingQuoteLine = z.infer<typeof BookingQuoteLine>

export const BookingRentalWorkbench = z.object({
  version: z.literal(1),
  asOf: Iso,
  record: RecordHead,
  quote: z.object({
    spaces: z.array(BookingQuoteSpace).max(200),
    lines: z.array(BookingQuoteLine).max(200),
    pricing: z.object({
      subtotalMinor: Minor,
      discountMinor: Minor,
      discountNote: Text(500).nullable(),
      taxRate: z.number(),
      totalMinor: Minor,
      depositMinor: Minor.nullable(),
      depositDueDate: Day.nullable(),
    }),
    estimate: z.object({ lastSentAt: Iso.nullable(), sentCount: z.number().int(), acceptedAt: Iso.nullable(), acceptedVersion: Text(20).nullable() }),
    // A recurring series' child occurrence can't be quoted on its own.
    editable: z.boolean(),
  }),
  // Booking's catalogs for the quote builder: rooms with their standard
  // rates, and the charge templates staff pick lines from.
  catalog: z.object({
    spaces: z.array(z.object({ spaceId: Id, venue: Text(200).nullable(), space: Text(200).nullable(), hourlyMinor: Minor.nullable(), halfDayMinor: Minor.nullable(), fullDayMinor: Minor.nullable() })).max(200),
    charges: z.array(z.object({ label: Text(200), category: Text(100).nullable(), unitMinor: Minor })).max(200),
  }),
  contract: z.object({
    // Generated: Booking rendered the contract PDF and it can be signed.
    generated: z.boolean(),
    lastSentAt: Iso.nullable(),
    signingOpen: z.boolean(),
    clientSignedAt: Iso.nullable(),
    clientSignedName: Text(200).nullable(),
    countersignedAt: Iso.nullable(),
    countersignedName: Text(200).nullable(),
    // The signature's evidence as Booking holds it (decision 23): the hash
    // of the document shown, where it was signed and whether its signed PDF
    // was produced. Hub keeps a reference, never a copy.
    evidence: z
      .object({ documentHash: Text(64), source: Text(40), signedAt: Iso.nullable(), pdfStatus: Text(20), hashVersion: z.number().int() })
      .nullable(),
    countersignature: z.object({ signedAt: Iso, name: Text(200).nullable(), documentHash: Text(64).nullable(), channel: Text(20) }).nullable(),
    canCountersign: z.boolean(),
    // Why not, in Booking's words (not signed yet, already countersigned).
    countersignBlocked: Text(300).nullable(),
    ticketedSetupMissing: z.array(Text(200)).max(20),
    packets: z.array(z.object({ sourceId: Id, title: Text(300), status: Text(40), sentAt: Iso.nullable(), clientSignedAt: Iso.nullable(), countersignedAt: Iso.nullable() })).max(50),
    url: Text(500),
  }),
  insurance: z.object({
    coiStatus: z.enum(['not_required', 'required', 'submitted', 'verified', 'rejected', 'expired']).nullable(),
    coiDocument: z.boolean(),
    insurer: Text(200).nullable(),
    hasPolicyNumber: z.boolean(),
    coverageMinor: Minor.nullable(),
    effectiveDate: Day.nullable(),
    expirationDate: Day.nullable(),
    verifiedAt: Iso.nullable(),
    verifiedByName: Text(200).nullable(),
    reviewNote: Text(1000).nullable(),
    lastRequestedAt: Iso.nullable(),
    liabilityOffered: z.boolean(),
    cancellationOffered: z.boolean(),
    policies: z.array(z.object({ product: Text(80).nullable(), status: Text(40), coverageMinor: Minor.nullable(), premiumMinor: Minor.nullable(), effectiveDate: Day.nullable(), expirationDate: Day.nullable(), boundAt: Iso.nullable() })).max(20),
    url: Text(500),
  }),
  documents: z.object({
    requests: z.array(z.object({ sourceId: Id, title: Text(200), description: Text(2000).nullable(), status: z.enum(['open', 'submitted', 'approved', 'waived']), dueDate: Day.nullable(), createdAt: Iso.nullable(), reviewedAt: Iso.nullable() })).max(200),
    files: z.array(z.object({ sourceId: Id, name: Text(300), uploadedBy: z.enum(['renter', 'bre']), requestId: Id.nullable(), createdAt: Iso.nullable() })).max(200),
    url: Text(500),
  }),
})
export type BookingRentalWorkbench = z.infer<typeof BookingRentalWorkbench>

export const BookingSponsorshipWorkbench = z.object({
  version: z.literal(1),
  asOf: Iso,
  record: RecordHead,
  agreement: z.object({
    status: Text(40).nullable(),
    sentAt: Iso.nullable(),
    signedAt: Iso.nullable(),
    termIds: z.array(Id).max(200),
    availableTerms: z.array(z.object({ id: Id, title: Text(300), category: Text(100).nullable(), defaultSelected: z.boolean() })).max(200),
    signers: z.array(z.object({ role: z.enum(['sponsor', 'bre']), status: Text(40), sentAt: Iso.nullable(), viewedAt: Iso.nullable(), signedAt: Iso.nullable() })).max(20),
    // Booking's own preconditions for sending (an email, cash or trade terms).
    canSend: z.boolean(),
    sendBlocked: Text(300).nullable(),
    url: Text(500),
  }),
  invoiceSeries: z.object({ canGenerate: z.boolean(), blocked: Text(300).nullable(), cadence: Text(20).nullable(), existingInvoices: z.number().int() }),
})
export type BookingSponsorshipWorkbench = z.infer<typeof BookingSponsorshipWorkbench>

// ------------------------------------------------------------------ money

export const BookingMoneyInvoice = z.object({
  sourceId: Id,
  number: Text(40).nullable(),
  status: z.enum(['draft', 'scheduled', 'sent', 'partial', 'paid', 'overdue', 'voided']),
  approvalStatus: z.enum(['draft', 'pending_review', 'approved', 'changes_requested']),
  totalMinor: Minor,
  paidMinor: Minor,
  // Null while nothing is billed (draft or scheduled): not billed is not
  // "owes nothing". Zero once voided.
  dueMinor: Minor.nullable(),
  issuedDate: Day.nullable(),
  dueDate: Day.nullable(),
  lines: z.array(z.object({ description: Text(500), quantity: z.number(), unitMinor: Minor, totalMinor: Minor })).max(100),
  collection: z.object({ status: z.enum(BOOKING_COLLECTION_STATUSES), lastContactedAt: Iso.nullable(), nextFollowUpOn: Day.nullable() }),
  checkPromisedAt: Iso.nullable(),
  lastSentAt: Iso.nullable(),
  reminders: z.array(z.object({ sentAt: Iso })).max(100),
  // A bank payment in flight (ACH): Booking's word for it and the amount.
  pendingPayment: z.object({ status: Text(40), amountMinor: Minor.nullable() }).nullable(),
  url: Text(500),
})
export type BookingMoneyInvoice = z.infer<typeof BookingMoneyInvoice>

export const BookingMoneyPayment = z.object({
  sourceId: Id,
  invoiceId: Id,
  amountMinor: Minor,
  // A word only: card, bank (ACH), check, cash, wire, other.
  method: z.enum(['card', 'bank', 'check', 'cash', 'wire', 'other']),
  status: z.enum(['pending', 'completed', 'failed', 'partially_refunded', 'refunded']),
  // A check number or wire reference (staff-entered). Never a card number.
  reference: Text(100).nullable(),
  receivedOn: Day.nullable(),
  receivedByName: Text(200).nullable(),
  paidAt: Iso.nullable(),
  refundedMinor: Minor,
  // What could still be refunded: amount less refunds settled or in flight.
  refundableMinor: Minor,
  // Whether a refund can go back through Stripe (a card or bank payment).
  viaStripe: z.boolean(),
})
export type BookingMoneyPayment = z.infer<typeof BookingMoneyPayment>

export const BookingMoneyRefund = z.object({
  sourceId: Id,
  paymentId: Id,
  invoiceId: Id,
  amountMinor: Minor,
  method: Text(20),
  status: z.enum(['pending', 'succeeded', 'failed']),
  failureMessage: Text(1000).nullable(),
  createdAt: Iso.nullable(),
  settledAt: Iso.nullable(),
  createdByName: Text(200).nullable(),
  // The Hub money request this refund carried out (its requestKey), or null
  // for one made in Booking.
  hubRequestKey: Text(120).nullable(),
})
export type BookingMoneyRefund = z.infer<typeof BookingMoneyRefund>

export const BookingRecordMoney = z.object({
  version: z.literal(1),
  asOf: Iso,
  record: RecordHead,
  // Four different numbers (spec §24), each Booking's: what was agreed, what
  // was billed, what came in, what is still owed. Never revenue.
  summary: z.object({
    contractedMinor: Minor.nullable(),
    invoicedMinor: Minor,
    collectedMinor: Minor,
    refundedMinor: Minor,
    balanceMinor: Minor.nullable(),
    pendingMinor: Minor,
    depositMinor: Minor.nullable(),
    depositDueDate: Day.nullable(),
    // The deposit counts as received once an invoice holding it is paid.
    depositReceived: z.boolean().nullable(),
  }),
  invoices: z.array(BookingMoneyInvoice).max(200),
  payments: z.array(BookingMoneyPayment).max(200),
  refunds: z.array(BookingMoneyRefund).max(200),
})
export type BookingRecordMoney = z.infer<typeof BookingRecordMoney>

// ------------------------------------------------------------ collections

// Every open rental or sponsorship invoice with a due date (sent, partial,
// overdue). Hub sorts them into aging buckets and works out the next action
// (collectionsView in this file), so the math is tested in one place.
export const BookingCollectionsInvoice = z.object({
  sourceId: Id,
  number: Text(40).nullable(),
  record: z.object({ kind: z.enum(['rental', 'sponsorship']), id: Id, title: Text(300) }),
  billTo: Text(300).nullable(),
  status: z.enum(['sent', 'partial', 'overdue']),
  totalMinor: Minor,
  dueMinor: Minor,
  issuedDate: Day.nullable(),
  dueDate: Day,
  collection: z.object({ status: z.enum(BOOKING_COLLECTION_STATUSES), lastContactedAt: Iso.nullable(), nextFollowUpOn: Day.nullable() }),
  checkPromisedAt: Iso.nullable(),
  reminders: z.object({ count: z.number().int(), lastAt: Iso.nullable() }),
  lastActivity: z.object({ at: Iso, title: Text(300) }).nullable(),
  openTasks: z.number().int(),
  pendingPayment: z.object({ status: Text(40), amountMinor: Minor.nullable() }).nullable(),
  url: Text(500),
})
export type BookingCollectionsInvoice = z.infer<typeof BookingCollectionsInvoice>

export const BookingCollections = z.object({ version: z.literal(1), asOf: Iso, today: Day, invoices: z.array(BookingCollectionsInvoice).max(1000) })
export type BookingCollections = z.infer<typeof BookingCollections>

export const BookingRefundByKey = z.object({ version: z.literal(1), asOf: Iso, refund: BookingMoneyRefund.extend({ record: z.object({ kind: z.enum(['rental', 'sponsorship']), id: Id }).nullable() }).nullable() })
export type BookingRefundByKey = z.infer<typeof BookingRefundByKey>

// ------------------------------------------------- collections, Hub's math

export const AGING_BUCKETS = ['current', '1-30', '31-60', '61-90', '90+'] as const
export type AgingBucket = (typeof AGING_BUCKETS)[number]

const dayNumber = (d: string) => Math.floor(Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10))) / 86_400_000)

// Days past due on `today` (Booking's local date), never negative.
export function daysPastDue(dueDate: string, today: string): number {
  return Math.max(0, dayNumber(today) - dayNumber(dueDate))
}

export function agingBucket(dueDate: string, today: string): AgingBucket {
  const d = daysPastDue(dueDate, today)
  if (d === 0) return 'current'
  if (d <= 30) return '1-30'
  if (d <= 60) return '31-60'
  if (d <= 90) return '61-90'
  return '90+'
}

// A promised check holds collections off for 7 days (Booking's rule).
export const CHECK_PROMISE_GRACE_DAYS = 7

export type CollectionsNextAction = { kind: 'none' | 'wait_check' | 'wait_bank' | 'follow_up_due' | 'send_reminder' | 'escalate' | 'review_dispute' | 'written_off'; label: string; dueOn: string | null }

// What staff should do next about one invoice, from Booking's facts. Hub
// tasks follow up; nothing here changes a balance (spec §20).
export function collectionsNextAction(inv: Pick<BookingCollectionsInvoice, 'dueDate' | 'collection' | 'checkPromisedAt' | 'reminders' | 'pendingPayment'>, today: string): CollectionsNextAction {
  const past = daysPastDue(inv.dueDate, today)
  if (inv.collection.status === 'uncollectible') return { kind: 'written_off', label: 'Marked uncollectible', dueOn: null }
  if (inv.collection.status === 'dispute') return { kind: 'review_dispute', label: 'Resolve the dispute', dueOn: inv.collection.nextFollowUpOn }
  if (inv.pendingPayment) return { kind: 'wait_bank', label: 'A bank payment is in flight', dueOn: null }
  if (inv.checkPromisedAt) {
    const promised = inv.checkPromisedAt.slice(0, 10)
    if (Day.safeParse(promised).success && daysPastDue(promised, today) < CHECK_PROMISE_GRACE_DAYS) {
      const until = new Date(Date.UTC(Number(promised.slice(0, 4)), Number(promised.slice(5, 7)) - 1, Number(promised.slice(8, 10)) + CHECK_PROMISE_GRACE_DAYS)).toISOString().slice(0, 10)
      return { kind: 'wait_check', label: 'A check is promised', dueOn: until }
    }
  }
  if (inv.collection.nextFollowUpOn && inv.collection.nextFollowUpOn > today) return { kind: 'none', label: 'Follow-up scheduled', dueOn: inv.collection.nextFollowUpOn }
  if (inv.collection.nextFollowUpOn && inv.collection.nextFollowUpOn <= today) return { kind: 'follow_up_due', label: 'Follow-up is due', dueOn: inv.collection.nextFollowUpOn }
  if (past === 0) return { kind: 'none', label: 'Not due yet', dueOn: inv.dueDate }
  if (past > 60 && inv.collection.status !== 'escalated') return { kind: 'escalate', label: 'Over 60 days past due: escalate', dueOn: today }
  const last = inv.reminders.lastAt ? inv.reminders.lastAt.slice(0, 10) : null
  if (!last || daysPastDue(last, today) >= 7) return { kind: 'send_reminder', label: last ? 'No reminder in 7 days' : 'No reminder sent yet', dueOn: today }
  return { kind: 'none', label: 'Reminder sent recently', dueOn: null }
}

export type CollectionsSummary = { buckets: Record<AgingBucket, { count: number; dueMinor: number }>; totalDueMinor: number; overdueCount: number }

export function collectionsSummary(invoices: Pick<BookingCollectionsInvoice, 'dueDate' | 'dueMinor'>[], today: string): CollectionsSummary {
  const buckets = Object.fromEntries(AGING_BUCKETS.map((b) => [b, { count: 0, dueMinor: 0 }])) as Record<AgingBucket, { count: number; dueMinor: number }>
  let totalDueMinor = 0
  let overdueCount = 0
  for (const inv of invoices) {
    const b = agingBucket(inv.dueDate, today)
    buckets[b].count += 1
    buckets[b].dueMinor += inv.dueMinor
    totalDueMinor += inv.dueMinor
    if (b !== 'current') overdueCount += 1
  }
  return { buckets, totalDueMinor, overdueCount }
}

// ------------------------------------------------------------- refund math

// What could still be refunded on one payment, as Booking counts it: the
// amount less refunds settled or still in flight. Hub checks a request
// against Booking's read; Booking checks again inside the command.
export function refundableMinor(p: { amountMinor: number; refunds: { amountMinor: number; status: 'pending' | 'succeeded' | 'failed' }[] }): number {
  const committed = p.refunds.filter((r) => r.status !== 'failed').reduce((s, r) => s + r.amountMinor, 0)
  return Math.max(0, p.amountMinor - committed)
}

// Who sees amounts on rentals and sponsorships: Event 360's rule (finance.read
// or events.settle, ADR 0041) plus the people who work the money (Phase 5).
export const canSeeRentalMoney = (caps: readonly string[]) =>
  caps.includes('hub.admin') || caps.includes('finance.read') || caps.includes('events.settle') || caps.includes('rentals.money') || caps.includes('refunds.approve')

// ------------------------------------------------------------ Hub's views

// The rental's quote and paperwork as Hub's page gets it. The quote and the
// catalog's rates are amounts: shown to people who may see rental money or
// build quotes (rentals.manage), null for everyone else.
export type BookingRentalWorkbenchView = Omit<BookingRentalWorkbench, 'quote' | 'catalog'> & {
  quote: BookingRentalWorkbench['quote'] | null
  catalog: BookingRentalWorkbench['catalog'] | null
  money: 'shown' | 'hidden'
}

export type CollectionsRow = BookingCollectionsInvoice & { bucket: AgingBucket; daysPastDue: number; next: CollectionsNextAction }
export type CollectionsView = { asOf: string; today: string; summary: CollectionsSummary; invoices: CollectionsRow[] }

export function collectionsView(data: BookingCollections): CollectionsView {
  const today = data.today
  const invoices = data.invoices
    .map((inv) => ({ ...inv, bucket: agingBucket(inv.dueDate, today), daysPastDue: daysPastDue(inv.dueDate, today), next: collectionsNextAction(inv, today) }))
    .sort((a, b) => b.daysPastDue - a.daysPastDue || b.dueMinor - a.dueMinor)
  return { asOf: data.asOf, today, summary: collectionsSummary(data.invoices, today), invoices }
}

// The rental's own client fields, for Change renter (ADR 0078): apart from the
// workbench, and passed on only to rentals.manage.
export const BookingRentalRenter = z.object({ version: z.literal(1), asOf: Iso, name: Text(200), email: Text(320), phone: Text(40).nullable(), organization: Text(200).nullable() })
export type BookingRentalRenter = z.infer<typeof BookingRentalRenter>
